import axios from "axios";
import io from "socket.io-client";

import { truncate } from "@/utils/string.js";
import { getPval, get3Point } from "@/utils/statistics.js";
import Swal from "sweetalert2";
import ScaleLoader from "vue-spinner/src/ScaleLoader.vue";

import {
  buildGlmmPlot,
  buildGwasPlot,
  buildGlmmGwasPlot,
} from "@/plots/pheno/scatter.js";
import { buildPhenoTableData } from "@/table/pheno/pheno.js";
import { buildRegPlot } from "@/plots/regression/regchart.js";

export default {
  name: "PhenoKit",
  data() {
    return {
      id: null,
      glmmData: null,
      gwasData: null,
      metaInfo: null,
      clientId: "",
      regIsDark: true,
      regIsFullRange: false,
      cache: {},
      socket: null,
      selectedImpacts: ["MODIFIER", "LOW", "MODERATE", "HIGH"],
      showPrediction: true,
      showObserved: true,
      showEEF: true,
      showWHG: true,
      showSTEPPE: true,
      derivedAlleleFreq: null,
      selectedPhenoId: "",
      loadingDownload: false,
      loadingScatterDownload: false,
      gridLoading: "loading",
      gridColor: "#A51C30",
      gridSize: "60px",
      regChart: null,
      computation: {
        gwas: "PROCESS",
      },
    };
  },
  components: {
    ScaleLoader,
  },
  methods: {
    getPval(pval) {
      return getPval(pval);
    },
    async downloadTSV() {
      let ID = this.id;
      this.loadingDownload = true;
      this.axiosHandler()
        .post(
          `/api/download-pheno-table`,
          {
            id: ID,
            client_id: this.clientId,
          },
          { responseType: "blob" }
        )
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "text/tab-separated-values",
          });
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `PheWAS-${ID}.tsv`);
          document.body.appendChild(link);
          link.click();

          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Error downloading the file:", error);
        })
        .finally(() => {
          this.loadingDownload = false;
        });
    },
    async downloadScatter() {
      let ID = this.id;
      let phenoId = this.selectedPhenoId;
      this.loadingScatterDownload = true;
      this.axiosHandler()
        .post(
          `/api/download-scatter-data`,
          {
            id: ID,
            pheno_id: phenoId,
            client_id: this.clientId,
          },
          { responseType: "blob" }
        )
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "text/tab-separated-values",
          });
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Scatter-${ID}-Pheno(${phenoId}).tsv`);
          document.body.appendChild(link);
          link.click();

          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Error downloading the file:", error);
        })
        .finally(() => {
          this.loadingScatterDownload = false;
        });
    },
    get3Point(number) {
      return get3Point(number);
    },
    truncate(text) {
      return truncate(text);
    },
    setGlmmPlot() {
      let glmContainer = document.getElementById("glm-plot");
      let id = this.id;
      buildGlmmPlot(
        glmContainer,
        this.gwasData,
        id,
        this.plotClickHandler,
        this.selectedImpacts
      );
    },
    setGwasPlot() {
      let gwasContainer = document.getElementById("gwas-plot");
      let id = this.id;
      buildGwasPlot(
        gwasContainer,
        this.gwasData,
        id,
        this.plotClickHandler,
        this.selectedImpacts
      );
    },
    setGlmmGwasPlot() {
      let gwasGlmContainer = document.getElementById("glm-gwas-plot");
      let id = this.id;
      buildGlmmGwasPlot(
        gwasGlmContainer,
        this.gwasData,
        id,
        this.plotClickHandler,
        this.selectedImpacts
      );
    },
    buildRegPlotHandler() {
      let regContainer = document.getElementById("reg-plot");
      regContainer.innerHTML = "";
      this.regChart = buildRegPlot(
        regContainer,
        this.gtSlope,
        this.gtBeta,
        this.regIsDark,
        this.derivedAlleleFreq,
        this.regIsFullRange
      );
      if (!this.showEEF) {
        this.toggleRegChart("EEF");
      }
      if (!this.showWHG) {
        this.toggleRegChart("WHG");
      }
      if (!this.showSTEPPE) {
        this.toggleRegChart("STEPPE");
      }
      if (!this.showObserved) {
        this.toggleRegChart("Observed");
      }
      if (!this.showPrediction) {
        this.toggleRegChart("Model Prediction");
      }
    },
    toggleRegChart(series) {
      this.regChart.toggleSeries(series);
      this.regChart.toggleSeries(series + " Range");
    },
    toggleObserved() {
      this.showObserved = !this.showObserved;
      this.toggleRegChart("Observed");
      let legend = document.querySelector(".legend-item-observed");
      legend.style.backgroundColor = this.showObserved ? "#4E84C4" : "gray";
      legend.style.color = this.showObserved ? "#4E84C4" : "gray";
    },
    togglePrediction() {
      this.showPrediction = !this.showPrediction;
      this.toggleRegChart("Model Prediction");
      let legend = document.querySelector(".legend-item-prediction");
      legend.style.backgroundColor = this.showPrediction ? "#D16103" : "gray";
      legend.style.color = this.showPrediction ? "#D16103" : "gray";
    },
    toggleEEF() {
      this.showEEF = !this.showEEF;
      this.toggleRegChart("EEF");
      let legend = document.querySelector(".legend-item-eef");
      legend.style.backgroundColor = this.showEEF ? "#e8bd10" : "gray";
      legend.style.color = this.showEEF ? "#e8bd10" : "gray";
    },
    toggleWHG() {
      this.showWHG = !this.showWHG;
      this.toggleRegChart("WHG");
      let legend = document.querySelector(".legend-item-whg");
      legend.style.backgroundColor = this.showWHG ? "#3fb543" : "gray";
      legend.style.color = this.showWHG ? "#3fb543" : "gray";
    },
    toggleSteppe() {
      this.showSTEPPE = !this.showSTEPPE;
      this.toggleRegChart("STEPPE");
      let legend = document.querySelector(".legend-item-steppe");
      legend.style.backgroundColor = this.showSTEPPE ? "#fa3c6c" : "gray";
      legend.style.color = this.showSTEPPE ? "#fa3c6c" : "gray";
    },
    activeAllLegend() {
      const l1 = document.querySelector(".legend-item-steppe");
      l1.style.backgroundColor = "#fa3c6c";
      l1.style.color = "#fa3c6c";

      const l2 = document.querySelector(".legend-item-whg");
      l2.style.backgroundColor = "#3fb543";
      l2.style.color = "#3fb543";

      const l3 = document.querySelector(".legend-item-eef");
      l3.style.backgroundColor = "#e8bd10";
      l3.style.color = "#e8bd10";

      const l4 = document.querySelector(".legend-item-prediction");
      l4.style.backgroundColor = "#D16103";
      l4.style.color = "#D16103";

      const l5 = document.querySelector(".legend-item-observed");
      l5.style.backgroundColor = "#4E84C4";
      l5.style.color = "#4E84C4";
    },
    plotClickHandler(item) {
      const id = item.data[3];
      let routeData = this.$router.resolve({
        name: "Charts",
        params: { id },
      });
      window.open(routeData.href, "_blank");
    },
    axiosHandler() {
      const axiosInstance = axios.create({
        baseURL: process.env.VUE_APP_ROOT_API,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "ages_hms_access_token"
          )}`,
        },
      });
      return axiosInstance;
    },
    updatePlots() {
      let phenoId = this.selectedPhenoId;
      let ID = this.id;
      this.axiosHandler()
        .post(`/api/get-pheno-gwas-json`, {
          id: ID,
          pheno_id: phenoId,
          client_id: localStorage.getItem("client_id"),
        })
        .then((response) => {
          if (response.data.has_time == false) {
            Swal.fire({
              customClass: {
                title: "swal-title",
                text: "swal-text",
                confirmButton: "swal-confirm-button",
              },
              title: '<p style="font-family: Ubuntu">Session Expired</p>',
              html: '<p style="font-family:Ubuntu; font-size: 16px;">Your session has expired. <br>Please refresh the page.</p>',
              icon: "warning",
              iconColor: "#A51C30",
              confirmButtonText: "Close",
              allowOutsideClick: false,
            });
          }
          this.gwasData = response.data.gwas_json;
          this.setGlmmPlot();
          this.setGwasPlot();
          this.setGlmmGwasPlot();
          this.computation.gwas = "FINISHED";
        })
        .catch((response) => {
          console.log(response);
          this.computation.gwas = "FINISHED";
        });
    },
    updatePlotByClick(pheno_id) {
      this.selectedPhenoId = pheno_id;
      this.computation.gwas = "PROCESS";
      this.updatePlots();
    },
    buildPhenoTable() {
      let searchUrl = process.env.VUE_APP_ROOT_API + "/api/get-pheno-table";
      buildPhenoTableData(this.updatePlotByClick, "table", searchUrl, this.id);
    },
    resetSelection() {
      let glmContainer = document.getElementById("glm-plot");
      buildGlmmPlot(glmContainer, null, null, null, null, true);
    },
    resetGwas() {
      let glmContainer = document.getElementById("gwas-plot");
      buildGwasPlot(glmContainer, null, null, null, null, true);
    },
    resetSelectionGwas() {
      let glmContainer = document.getElementById("glm-gwas-plot");
      buildGlmmGwasPlot(glmContainer, null, null, null, null, true);
    },
    updateImpact() {
      this.selectedImpacts = Array.from(
        document.querySelectorAll(".impact-checkbox")
      )
        .filter((checkbox) => checkbox.checked)
        .map((checkbox) => checkbox.value);
      this.setGlmmPlot();
      this.setGwasPlot();
      this.setGlmmGwasPlot();
    },
    infoSelection() {
      Swal.fire({
        width: 500,
        customClass: {
          title: "swal-title",
          text: "swal-text",
          confirmButton: "swal-confirm-button",
        },
        confirmButtonColor: "#A51C30",
        confirmButtonText: "Close",
        title:
          '<h6 style="font-family: Ubuntu; font-size: 20px;">Selection Plot</h6>',
        html: `<p style="text-align: left; font-family: Ubuntu; font-size: 15px; text-align: justify;"> P-value quantifying the statistical significance of this SNP's selection statistic.
          Converted to a -log<sub>10</sub>
          scale to make the more significant values larger and the less significant values smaller.</p>`,
      });
    },
    infoGwas() {
      Swal.fire({
        width: 500,
        customClass: {
          title: "swal-title",
          text: "swal-text",
          confirmButton: "swal-confirm-button",
        },
        confirmButtonColor: "#A51C30",
        confirmButtonText: "Close",
        title:
          '<h6 style="font-family: Ubuntu; font-size: 20px;">GWAS Plot</h6>',
        html: `<p style="text-align: justify; font-family: Ubuntu; font-size: 15px;">
  The P-value quantifies the statistical significance of the variant's association with the specified phenotype. It is converted to a -log<sub>10</sub> scale, where more significant values appear larger, and less significant values appear smaller.
</p>`,
      });
    },
    infoSelectionGwas() {
      Swal.fire({
        width: 850,
        customClass: {
          title: "swal-title",
          text: "swal-text",
          confirmButton: "swal-confirm-button",
        },
        confirmButtonColor: "#A51C30",
        confirmButtonText: "Close",
        title:
          '<h6 style="font-family: Ubuntu; font-size: 20px;">Selection vs. GWAS Plot</h6>',
        html: `<p style="text-align: left; font-family: Ubuntu; font-size: 15px; text-align: justify; line-height: 28px;">
          This plot shows if a variant under selection is significantly linked to a phenotype and whether the selection and GWAS signals overlap:
        </p>
        <p style="text-align: left; font-family: Ubuntu; font-size: 15px; line-height: 28px;">
          <b>X-axis:</b> -log<sub>10</sub> P-value for SNP-phenotype association.
        </p>
        <p style="text-align: left; font-family: Ubuntu; font-size: 15px; line-height: 28px;">
          <b>Y-axis:</b> Selection P-value, adjusted for the direction of selection (s) and phenotype effect size (β).
        </p>
        <br>
        <p style="text-align: left; font-family: Ubuntu; font-size: 15px; line-height: 28px;">
          <b>Positive y-axis:</b> Selection increases the phenotype (sβ > 0) by:
        </p>
        <p style="text-align: left; font-family: Ubuntu; font-size: 15px; line-height: 28px; margin-left: 20px;">
          - Increasing the frequency of an allele that enhances it (s > 0, β > 0).
        </p>
        <p style="text-align: left; font-family: Ubuntu; font-size: 15px; line-height: 28px; margin-left: 20px;">
          - Decreasing the frequency of an allele that reduces it (s < 0, β < 0).
        </p>
        <p style="text-align: left; font-family: Ubuntu; font-size: 15px; line-height: 28px;">
          <b>Negative y-axis:</b> Selection decreases the phenotype (sβ < 0) by:
        </p>
        <p style="text-align: left; font-family: Ubuntu; font-size: 15px; line-height: 28px; margin-left: 20px;">
          - Decreasing the frequency of an allele that enhances it (s < 0, β > 0).
        </p>
        <p style="text-align: left; font-family: Ubuntu; font-size: 15px; line-height: 28px; margin-left: 20px;">
          - Increasing the frequency of an allele that reduces it (s > 0, β < 0).
        </p>`,
      });
    },
  },
  mounted() {
    document.querySelectorAll(".impact-checkbox").forEach((checkbox) => {
      checkbox.addEventListener("change", this.updateImpact);
    });
    document.querySelector(".expand-reg").addEventListener("click", () => {
      this.regIsFullRange = !this.regIsFullRange;
      this.buildRegPlotHandler();
    });
    document.querySelector(".reset-reg").addEventListener("click", () => {
      this.regIsFullRange = false;
      this.showEEF = true;
      this.showObserved = true;
      this.showPrediction = true;
      this.showSTEPPE = true;
      this.showWHG = true;
      this.activeAllLegend();
      this.buildRegPlotHandler();
    });
    document
      .querySelector(".legend-item-observed")
      .addEventListener("click", () => {
        this.toggleObserved();
      });
    document
      .querySelector(".legend-item-prediction")
      .addEventListener("click", () => {
        this.togglePrediction();
      });
    document.querySelector(".legend-item-eef").addEventListener("click", () => {
      this.toggleEEF();
    });
    document.querySelector(".legend-item-whg").addEventListener("click", () => {
      this.toggleWHG();
    });
    document
      .querySelector(".legend-item-steppe")
      .addEventListener("click", () => {
        this.toggleSteppe();
      });
    document.getElementById("darkModer").addEventListener("click", () => {
      this.regIsDark = !this.regIsDark;
      this.buildRegPlotHandler();
      document.querySelectorAll(".legend").forEach((legend) => {
        legend.style.color = this.regIsDark ? "white" : "black";
      });
      document.querySelector(".download-reg").style.color = this.regIsDark
        ? "white"
        : "rgb(102, 102, 102)";
      document.querySelector(".expand-reg").style.color = this.regIsDark
        ? "white"
        : "rgb(102, 102, 102)";
      document.querySelector(".reset-reg").style.color = this.regIsDark
        ? "white"
        : "rgb(102, 102, 102)";
      document.querySelector(".download-reg-tsv").style.color = this.regIsDark
        ? "white"
        : "rgb(102, 102, 102)";
      document.getElementById("reg-info").style.backgroundColor = this.regIsDark
        ? "#1E1E1E"
        : "white";
    });
    this.gtSlope = this.$store.state.gtSlope;
    this.gtBeta = this.$store.state.gtBeta;
    this.derivedAlleleFreq = this.$store.state.derivedAlleleFreq;
    this.buildRegPlotHandler();
    this.glmmData = this.$store.state.glmmData;
    this.metaInfo = this.$store.state.metaInfo;
    this.id = this.$store.state.id;
    this.selectedPhenoId = this.$store.state.firstPhenoId;
    this.buildPhenoTable();
    this.updatePlots();
  },
};
